import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { useAuth } from '@/features/auth/useAuth';

/**
 * Mirrors App\Http\Requests\Auth\RegisterRequest.
 *
 * Client-side validation is for fast feedback only — the server validates
 * independently and its errors are mapped back onto these fields.
 */
const schema = z
  .object({
    name: z.string().min(1, 'Your name is required').max(255),
    email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
    organization_name: z.string().min(1, 'Tell us your company or team name').max(255),
    // 12 characters, matching Password::min(12) on the server. The server also
    // checks it against known breached passwords, which cannot be done here.
    password: z.string().min(12, 'Use at least 12 characters'),
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type FormValues = z.infer<typeof schema>;

export function RegisterPage() {
  const { register: registerAccount } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setFormError(null);

    try {
      await registerAccount(values);
      void navigate('/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        const fieldErrors = error.fieldErrors;
        const keys = Object.keys(fieldErrors);

        if (keys.length > 0) {
          for (const key of keys) {
            setError(key as keyof FormValues, {
              message: fieldErrors[key]?.[0] ?? 'Invalid value',
            });
          }
        } else {
          setFormError(error.detail ?? error.title);
        }
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  };

  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h1">Create your workspace</Typography>
            <Typography variant="body2" color="text.secondary">
              You will be the owner of this organisation.
            </Typography>
          </Stack>

          {formError && (
            <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}

          <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Your name"
                autoComplete="name"
                autoFocus
                fullWidth
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                {...register('name')}
              />

              <TextField
                label="Work email"
                type="email"
                autoComplete="email"
                fullWidth
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                {...register('email')}
              />

              <Divider flexItem />

              <TextField
                label="Company or team name"
                fullWidth
                error={Boolean(errors.organization_name)}
                helperText={errors.organization_name?.message}
                {...register('organization_name')}
              />

              <Divider flexItem />

              <TextField
                label="Password"
                type="password"
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.password)}
                helperText={errors.password?.message ?? 'At least 12 characters'}
                {...register('password')}
              />

              <TextField
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                fullWidth
                error={Boolean(errors.password_confirmation)}
                helperText={errors.password_confirmation?.message}
                {...register('password_confirmation')}
              />

              <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                {isSubmitting ? 'Creating…' : 'Create workspace'}
              </Button>
            </Stack>
          </form>

          <Typography variant="body2" sx={{ mt: 3 }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">
              Sign in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
